/**
 * TeamsManager – Superadmin-only component for managing teams and their members.
 *
 * Only rendered when isSuperAdmin === true.
 * Lets the superadmin:
 *  - See all existing teams
 *  - Create a new team
 *  - Add / remove members (by email) from any team
 *  - Delete a team
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Team,
  listTeams,
  createTeam,
  deleteTeam,
  addTeamMember,
  removeTeamMember,
} from '../services/api';

interface TeamsManagerProps {
  onClose: () => void;
}

const TeamsManager: React.FC<TeamsManagerProps> = ({ onClose }) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create team form
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // Add member form per team (teamId -> email)
  const [memberInputs, setMemberInputs] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listTeams();
      setTeams(res.teams);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Error loading teams');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateTeam = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await createTeam(newName.trim(), newDesc.trim());
      setNewName('');
      setNewDesc('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Error creating team');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTeam = async (teamId: string, teamName: string) => {
    if (!window.confirm(`¿Eliminar el equipo "${teamName}"? Los proyectos asociados quedarán sin equipo.`)) return;
    try {
      await deleteTeam(teamId);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Error deleting team');
    }
  };

  const handleAddMember = async (teamId: string) => {
    const email = (memberInputs[teamId] || '').trim().toLowerCase();
    if (!email) return;
    try {
      await addTeamMember(teamId, email);
      setMemberInputs(prev => ({ ...prev, [teamId]: '' }));
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Error adding member');
    }
  };

  const handleRemoveMember = async (teamId: string, memberEmail: string) => {
    if (!window.confirm(`¿Remover a "${memberEmail}" del equipo?`)) return;
    try {
      await removeTeamMember(teamId, memberEmail);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Error removing member');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-2xl">👥</span>
            <h2 className="text-xl font-bold text-gray-800">Gestión de Equipos</h2>
            <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-700 rounded-full">
              SUPERADMIN
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            ✕
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-6">
          {/* Error banner */}
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex justify-between">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-2 font-bold">✕</button>
            </div>
          )}

          {/* Create team form */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <h3 className="font-semibold text-gray-700 mb-3">➕ Nuevo Equipo</h3>
            <div className="flex flex-col gap-2">
              <input
                type="text"
                placeholder="Nombre del equipo *"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
              <input
                type="text"
                placeholder="Descripción (opcional)"
                value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
              <button
                onClick={handleCreateTeam}
                disabled={creating || !newName.trim()}
                className="self-end px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium
                  hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {creating ? 'Creando...' : 'Crear equipo'}
              </button>
            </div>
          </div>

          {/* Teams list */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">Cargando equipos...</div>
          ) : teams.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No hay equipos todavía. Crea el primero arriba.
            </div>
          ) : (
            <div className="space-y-4">
              {teams.map(team => (
                <div key={team.id} className="border border-gray-200 rounded-xl overflow-hidden">
                  {/* Team header */}
                  <div className="flex items-center justify-between bg-gradient-to-r from-purple-50 to-blue-50 px-4 py-3">
                    <div>
                      <p className="font-semibold text-gray-800">{team.name}</p>
                      {team.description && (
                        <p className="text-xs text-gray-500">{team.description}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteTeam(team.id, team.name)}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                      title="Eliminar equipo"
                    >
                      🗑️
                    </button>
                  </div>

                  {/* Members */}
                  <div className="px-4 py-3 space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Miembros ({team.members.length})
                    </p>
                    {team.members.length === 0 ? (
                      <p className="text-sm text-gray-400 italic">Sin miembros aún</p>
                    ) : (
                      <ul className="space-y-1">
                        {team.members.map(member => (
                          <li key={member} className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2">
                              <span className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-700">
                                {member[0].toUpperCase()}
                              </span>
                              {member}
                            </span>
                            <button
                              onClick={() => handleRemoveMember(team.id, member)}
                              className="text-xs text-red-400 hover:text-red-600 px-2 py-0.5 rounded hover:bg-red-50 transition"
                            >
                              Remover
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Add member input */}
                    <div className="flex gap-2 pt-1">
                      <input
                        type="email"
                        placeholder="email@empresa.com"
                        value={memberInputs[team.id] || ''}
                        onChange={e =>
                          setMemberInputs(prev => ({ ...prev, [team.id]: e.target.value }))
                        }
                        onKeyDown={e => e.key === 'Enter' && handleAddMember(team.id)}
                        className="flex-1 border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                      />
                      <button
                        onClick={() => handleAddMember(team.id)}
                        disabled={!(memberInputs[team.id] || '').trim()}
                        className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg
                          hover:bg-purple-700 disabled:opacity-50 transition"
                      >
                        + Añadir
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border rounded-lg hover:bg-gray-50 transition"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamsManager;
